import { useParams, Link } from "react-router-dom";
import { PenLine, ShieldCheck, ArrowLeft } from "lucide-react";
import SEO from "../components/SEO";
import CTASection from "../components/sections/CTASection";
import { AUTHORS, authorUrl } from "../config/authors";
import { SITE_URL } from "../config/site";

export default function AuthorProfile() {
  const { slug } = useParams();
  const author = AUTHORS[slug];

  if (!author) return (
    <div className="py-32 text-center">
      <SEO title="Author not found" description="This author page could not be found." noindex />
      <h1 className="text-3xl font-display font-bold text-forest mb-3">Author not found</h1>
      <Link to="/blog" className="btn-primary">Back to blog</Link>
    </div>
  );

  const schema = {
    "@context": "https://schema.org",
    "@type": "ProfilePage",
    "mainEntity": {
      "@type": "Person",
      "name": author.name,
      "url": authorUrl(author.slug),
      "jobTitle": author.role,
      "description": author.bio,
      "worksFor": {
        "@type": "Organization",
        "name": "Indiana Accident Aid",
        "url": SITE_URL,
        ...(author.isFounder ? { "founder": { "@type": "Person", "name": author.name } } : {}),
      },
    },
  };

  return (
    <>
      <SEO
        title={`${author.name} — ${author.role} | Indiana Accident Aid`}
        description={author.bio}
        type="profile"
        schema={schema}
        canonical={authorUrl(author.slug)}
      />
      <section className="pt-12 md:pt-20 pb-12 px-6 bg-bone" data-testid="author-profile">
        <div className="max-w-3xl mx-auto">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-semibold text-charcoal-soft hover:text-terracotta mb-6">
            <ArrowLeft className="w-4 h-4" /> Back to blog
          </Link>
          <div className="flex items-center gap-5">
            <div className="w-20 h-20 rounded-2xl bg-forest text-bone grid place-items-center font-display font-black text-2xl shrink-0" aria-hidden>
              {author.name.split(" ").map((n) => n[0]).join("")}
            </div>
            <div>
              <div className="overline mb-1">{author.role}</div>
              <h1 className="text-4xl md:text-5xl font-display font-bold text-forest tracking-tight">{author.name}</h1>
            </div>
          </div>
          <p className="text-lg md:text-xl text-charcoal-soft leading-relaxed mt-8" data-testid="author-bio">{author.bio}</p>

          <div className="grid sm:grid-cols-2 gap-4 mt-10">
            <Link to="/editorial-policy" className="card-elevated p-5 group" data-testid="author-editorial-link">
              <PenLine className="w-5 h-5 text-terracotta mb-2" />
              <div className="font-display font-bold text-forest group-hover:text-terracotta transition">Editorial Policy</div>
              <p className="text-sm text-charcoal-soft mt-1">How we research, write, and fact-check every Indiana guide.</p>
            </Link>
            <Link to="/corrections" className="card-elevated p-5 group" data-testid="author-corrections-link">
              <ShieldCheck className="w-5 h-5 text-terracotta mb-2" />
              <div className="font-display font-bold text-forest group-hover:text-terracotta transition">Corrections Policy</div>
              <p className="text-sm text-charcoal-soft mt-1">How to flag an error and how we handle corrections.</p>
            </Link>
          </div>

          <p className="text-xs text-charcoal-soft/80 leading-relaxed mt-10 border-t border-black/5 pt-6">
            Indiana Accident Aid publishes educational information about Indiana accident and injury
            topics. It is not a law firm and does not provide legal advice. Articles are written and
            edited for general information; where a qualified professional has reviewed a specific
            article, that reviewer is named on the article itself.
          </p>
        </div>
      </section>
      <CTASection />
    </>
  );
}
